<template>
  <div class="frame" :class="{ 'auth-active': activePanel }">
    <div class="bg-layer"></div>
    <div class="right-bg"></div>
    <MyNav class="nav-layer" />

    <transition name="banner-fade">
      <div v-if="maintenanceVisible" class="sys-banner sys-banner--maintenance">
        <i class="el-icon-warning-outline" />
        <span>系统维护中，部分功能暂不可用，敬请谅解</span>
      </div>
    </transition>
    <transition name="banner-fade">
      <div v-if="announcementVisible" class="sys-banner sys-banner--announce">
        <i class="el-icon-bell" />
        <span class="sys-banner__text">{{ publicSettings.announcementContent }}</span>
        <el-button
          v-if="publicSettings.announcementDismissible"
          size="mini"
          type="text"
          class="sys-banner__close"
          @click="dismissAnnouncement"
        >知道了</el-button>
      </div>
    </transition>

    <div class="main-area">
      <HomeContent :register-enabled="publicSettings.registerEnabled" @show-auth="showAuth" />
      <MyFoot />
    </div>

    <div class="auth-panel">
      <transition name="form-fade" mode="out-in">
        <LoginForm
          v-if="activePanel === 'login'"
          key="login"
          :prefill-account="prefillAccount"
          :login-enabled="publicSettings.loginEnabled"
          :register-enabled="publicSettings.registerEnabled"
          @login-success="onLoginSuccess"
          @show-register="showAuth('register')"
        />
        <RegisterForm
          v-else-if="activePanel === 'register'"
          key="register"
          :register-enabled="publicSettings.registerEnabled"
          :invite-code-required="publicSettings.inviteCodeRequired"
          @register-success="onRegisterSuccess"
          @show-login="showAuth('login')"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import MyNav from './MyNav.vue'
import HomeContent from './HomeContent.vue'
import MyFoot from './MyFoot.vue'
import LoginForm from './LoginForm.vue'
import RegisterForm from './RegisterForm.vue'
import { encryptPasswordForStorage } from '@/utils/crypto.js'
import { getPublicSettings } from '@/api/system.js'

export default {
  name: 'HomePage',
  components: {
    MyNav,
    HomeContent,
    MyFoot,
    LoginForm,
    RegisterForm
  },
  inject: ['setPage'],
  data() {
    return {
      activePanel: null,
      prefillAccount: '',
      // 系统公开配置（由后端 system_settings 驱动）。
      // 默认全开：配置接口挂了也不能把人挡在门外，最终仍以后端接口的裁决为准。
      publicSettings: {
        loginEnabled: true,
        registerEnabled: true,
        inviteCodeRequired: false,
        announcementEnabled: false,
        announcementContent: '',
        announcementDismissible: true,
        maintenanceEnabled: false
      },
      // 已关闭的公告内容（按内容记到 localStorage，新公告会重新出现）
      announcementDismissed: ''
    }
  },
  async created() {
    try {
      const data = await getPublicSettings()
      if (data && data.success && data.data) {
        const d = data.data
        this.publicSettings = {
          loginEnabled: d.loginEnabled !== false,
          registerEnabled: d.registerEnabled !== false,
          inviteCodeRequired: d.inviteCodeRequired === true,
          announcementEnabled: d.announcementEnabled === true,
          announcementContent: d.announcementContent || '',
          announcementDismissible: d.announcementDismissible !== false,
          maintenanceEnabled: d.maintenanceEnabled === true
        }
      }
    } catch (e) {
      // 拉取失败保持默认全开，不打断首屏
    }
    this.announcementDismissed = localStorage.getItem('tb_announce_dismissed') || ''
  },
  computed: {
    announcementVisible() {
      const ps = this.publicSettings
      if (!ps.announcementEnabled || !ps.announcementContent) return false
      if (ps.announcementDismissible && this.announcementDismissed === ps.announcementContent) return false
      return true
    },
    maintenanceVisible() {
      return !!this.publicSettings.maintenanceEnabled
    }
  },
  methods: {
    showAuth(type) {
      this.activePanel = this.activePanel === type ? null : type
      if (this.activePanel !== 'login') {
        this.prefillAccount = ''
      }
    },
    async onLoginSuccess(data) {
      localStorage.setItem('token', data.data.token)
      // 后端若在 user 对象里返回了 password，绝不能以明文落地 localStorage。
      // 用 PBKDF2 + salt 替换为 passwordHash / salt；不存在 password 则原样存。
      // ⚠️ 登录接口（LoginVO）是扁平结构：nickname / avatarUrl 直接挂在 data.data 上，
      //    没有 data.data.user 这个嵌套对象。之前这里读成了空对象并写回 localStorage，
      //    导致每次登录都会把本地缓存的头像清空（顶栏只能靠一次异步 getMe 补回来，
      //    请求慢或失败时头像就显示不出来 —— 这也是「头像有时消失」的原因之一）。
      const rawUser = data.data.user || {
        nickname: data.data.nickname || '',
        email: data.data.email || '',
        avatarUrl: data.data.avatarUrl || ''
      }
      const safeUser = { ...rawUser }
      if (typeof safeUser.password === 'string' && safeUser.password.length > 0) {
        const { passwordHash, salt } = await encryptPasswordForStorage(safeUser.password)
        safeUser.passwordHash = passwordHash
        safeUser.salt = salt
        delete safeUser.password
      }
      localStorage.setItem('user', JSON.stringify(safeUser))
      this.setPage('taskblock')
    },
    onRegisterSuccess(email) {
      this.prefillAccount = email
    },
    dismissAnnouncement() {
      this.announcementDismissed = this.publicSettings.announcementContent
      localStorage.setItem('tb_announce_dismissed', this.publicSettings.announcementContent)
    }
  }
}
</script>

<style scoped>
.frame{
  width:100%;
  height:100vh;
  border:20px solid #6a9854;
  border-radius:0;
  background:none;
  position:relative;
  overflow:hidden;
  padding:1.75rem 2.75rem 2.5rem;
  display:flex;
  flex-direction:column;
}

.bg-layer{
  position:absolute;
  top:0; left:0; right:0; bottom:0;
  background:url('https://image.suisuisu.cn/upload/2607/d1b502465354b537.png') no-repeat center center / cover;
  z-index:0;
  transition:transform 0.6s cubic-bezier(0.4,0,0.2,1);
}

.right-bg{
  position:absolute;
  top:0; right:0; bottom:0;
  width:50%;
  background:#fbf6f0;
  z-index:2;
  transform:translateX(100%);
  transition:transform 0.6s cubic-bezier(0.4,0,0.2,1);
}

.frame::after{
  content:"";
  position:absolute;
  top:0; left:0; right:0; bottom:0;
  background:rgba(247,237,225,0.5);
  z-index:1;
}
.nav-layer{
  position:relative;
  z-index:30;
}

.main-area{
  position:relative;
  z-index:3;
  width:100%;
  flex:1;
  min-height:0;
  display:flex;
  flex-direction:column;
}

.auth-panel{
  position:absolute;
  top:0;
  left:50%;
  right:0;
  bottom:0;
  background:#fff;
  transform:translateX(100%);
  transition:transform 0.6s cubic-bezier(0.4,0,0.2,1);
  z-index:10;
  padding:3rem 3.5rem;
  display:flex;
  align-items:stretch;
  justify-content:center;
  overflow:hidden;
}
.auth-panel > *{
  flex:0 1 auto;
  max-height:100%;
}

.frame.auth-active .bg-layer{
  transform:translateX(-50%);
}

.frame.auth-active .right-bg{
  transform:translateX(0);
}

.frame.auth-active .auth-panel{
  transform:translateX(0);
}

.form-fade-enter-active,
.form-fade-leave-active{
  transition:opacity 0.25s ease, transform 0.25s ease;
}

.form-fade-enter-from,
.form-fade-leave-to{
  opacity:0;
  transform:translateY(10px);
}

@media (max-width:900px){
  .right-bg{
    width:100%;
  }

  .auth-panel{
    left:0;
    right:0;
    min-width:auto;
    padding:2.5rem 2rem;
  }

  .frame.auth-active .bg-layer{
    transform:translateX(-100%);
  }

  /* 系统公告 / 维护横幅 */
  .sys-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    font-size: 14px;
    color: #fff;
    z-index: 20;
  }

  .sys-banner i {
    font-size: 16px;
  }

  .sys-banner__text {
    flex: 1;
    line-height: 1.6;
  }

  .sys-banner__close {
    color: #fff !important;
    border-color: rgba(255, 255, 255, 0.6) !important;
    margin-left: 8px;
  }

  .sys-banner--maintenance {
    background: #e6a23c;
  }

  .sys-banner--announce {
    background: #409eff;
  }

  .banner-fade-enter-active,
  .banner-fade-leave-active {
    transition: opacity 0.25s ease;
  }

  .banner-fade-enter,
  .banner-fade-leave-to {
    opacity: 0;
  }
}
</style>
